import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { CheckCircle2, Save, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ApiError, api } from '../lib/api';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatDateTime, formatNumber } from '../lib/format';
import { DIMENSIONS, type AiPointsConfig, type DimensionKey, type SettingsView } from '../lib/ai-points';
import { Button, Card, CardHeader, Checkbox, ErrorState, Field, Input, LoadingBlock, Select, Table, Td, Th, Tr, useToast } from './ui';

type NumberField = 'regularMinutesPerPoint' | 'freeMinutesPerPoint' | 'baselineWindowDays' | 'minBaselineDays' | 'minCallMinutes';
const NUMBER_FIELDS: Array<{ name: NumberField; min: number; max: number }> = [
  { name: 'regularMinutesPerPoint', min: 15, max: 480 },
  { name: 'freeMinutesPerPoint', min: 15, max: 480 },
  { name: 'baselineWindowDays', min: 7, max: 90 },
  { name: 'minBaselineDays', min: 1, max: 60 },
  { name: 'minCallMinutes', min: 1, max: 120 },
];

/**
 * AI Daily Points › Settings (CR-007 §8): the switch, the rubric's five maxima (whole points adding up
 * to 10), the thresholds the caps use, whether calls' existing AI summaries are read (their title and
 * counts only), and the model. Each save is a new version, audited, for the next runs only — a run keeps
 * the version it was made under. The schedule is fixed at 06:00 company time.
 */
export function AiPointsSettingsPanel() {
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['ai-points', 'settings'],
    queryFn: () => api.get<SettingsView>('/ai-daily-points/settings'),
  });
  const [draft, setDraft] = useState<AiPointsConfig | null>(null);
  const [problems, setProblems] = useState<Array<{ field: string; message: string }>>([]);
  useEffect(() => {
    if (data) setDraft(structuredClone(data.config));
    setProblems([]);
  }, [data]);

  const save = useMutation({
    mutationFn: () => api.put<SettingsView>('/ai-daily-points/settings', { config: draft }),
    onSuccess: (view) => {
      toast.success(t('aiPoints.settingsSaved', { v: view.version }));
      queryClient.setQueryData(['ai-points', 'settings'], view);
      void queryClient.invalidateQueries({ queryKey: ['ai-points', 'overview'] });
    },
    onError: (e: Error) => {
      const list = e instanceof ApiError ? ((e.details as { problems?: Array<{ field: string; message: string }> })?.problems ?? []) : [];
      setProblems(list);
      toast.error(e.message);
    },
  });

  if (isLoading || (!draft && !error)) return <LoadingBlock />;
  if (error || !data || !draft) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const sum = DIMENSIONS.reduce((s, k) => s + (Number(draft.rubric[k]) || 0), 0);
  const problemFor = (field: string) => problems.find((p) => p.field === field)?.message ?? null;
  const setRubric = (key: DimensionKey, value: string) =>
    setDraft({ ...draft, rubric: { ...draft.rubric, [key]: value === '' ? ('' as unknown as number) : Number(value) } });
  const changed = JSON.stringify(draft) !== JSON.stringify(data.config);

  return (
    <div className="max-w-4xl space-y-4">
      <Card>
        <div className="flex items-start gap-3">
          {data.server.ready ? (
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success-fg dark:text-success" aria-hidden />
          ) : (
            <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-error-fg dark:text-error-dark" aria-hidden />
          )}
          <div className="min-w-0 space-y-1 text-body-sm">
            <p className="font-medium text-[var(--text-primary)]">{t(data.server.ready ? 'aiPoints.serverReady' : 'aiPoints.blockedTitle')}</p>
            <p className="text-[var(--text-secondary)]">{t(data.server.ready ? 'aiPoints.serverReadyBody' : 'aiPoints.blockedBody')}</p>
            <p className="text-muted">
              {t('aiPoints.scheduleLine', { time: data.server.scheduleTime, timezone: data.server.timezone })}
              {!data.server.jobsEnabled && ` ${t('aiPoints.jobsOff')}`}
            </p>
          </div>
        </div>
      </Card>

      <Card>
        <CardHeader title={t('aiPoints.settingsTitle')} subtitle={t('aiPoints.settingsSubtitle', { v: data.version })} />
        <div className="space-y-5">
          <div>
            <Checkbox checked={draft.enabled} onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} label={t('aiPoints.enabledLabel')} />
            <p className="mt-1 text-caption text-faint">{t('aiPoints.enabledHint')}</p>
          </div>

          <fieldset>
            <legend className="mb-1 text-body-sm font-medium text-[var(--text-secondary)]">{t('aiPoints.rubricLabel')}</legend>
            <p className="mb-3 text-caption text-faint">{t('aiPoints.rubricHint')}</p>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              {DIMENSIONS.map((k) => (
                <Field key={k} label={t(`aiPoints.dim.${k}` as TranslationKey)} error={problemFor(`rubric.${k}`)}>
                  <Input type="number" min={0} max={10} step={1} inputMode="numeric" value={String(draft.rubric[k] ?? '')} onChange={(e) => setRubric(k, e.target.value)} />
                </Field>
              ))}
            </div>
            <p className={clsx('mt-2 text-body-sm tabular', sum === 10 ? 'text-success-fg dark:text-success' : 'text-error-fg dark:text-error-dark')}>
              {t('aiPoints.rubricSum', { n: formatNumber(sum, locale) })}
            </p>
            {problemFor('rubric') && <p className="text-caption text-error-fg dark:text-error-dark">{problemFor('rubric')}</p>}
          </fieldset>

          <div className="grid gap-4 sm:grid-cols-2">
            {NUMBER_FIELDS.map((f) => (
              <Field key={f.name} label={t(`aiPoints.setting.${f.name}` as TranslationKey)} hint={t(`aiPoints.setting.${f.name}Hint` as TranslationKey)} error={problemFor(f.name)}>
                <Input
                  type="number"
                  min={f.min}
                  max={f.max}
                  step={1}
                  inputMode="numeric"
                  value={String(draft[f.name] ?? '')}
                  onChange={(e) => setDraft({ ...draft, [f.name]: e.target.value === '' ? '' : Number(e.target.value) })}
                />
              </Field>
            ))}
            <Field label={t('aiPoints.setting.model')} hint={t('aiPoints.setting.modelHint')} error={problemFor('model')}>
              <Select value={draft.model} onChange={(e) => setDraft({ ...draft, model: e.target.value })}>
                <option value="">{t('aiPoints.setting.modelDefault', { model: data.server.models[0] ?? '—' })}</option>
                {data.server.models.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          <div>
            <Checkbox checked={draft.includeCallSummaries} onChange={(e) => setDraft({ ...draft, includeCallSummaries: e.target.checked })} label={t('aiPoints.setting.includeCallSummaries')} />
            <p className="mt-1 text-caption text-faint">{t('aiPoints.setting.includeCallSummariesHint')}</p>
          </div>

          <div className="flex justify-end gap-2 border-t hairline pt-4">
            <Button onClick={() => setDraft(structuredClone(data.defaults))}>{t('aiPoints.resetDefaults')}</Button>
            <Button variant="primary" icon={<Save className="h-4 w-4" />} loading={save.isPending} disabled={!changed || sum !== 10} onClick={() => save.mutate()}>
              {t('common.save')}
            </Button>
          </div>
        </div>
      </Card>

      <Card padded={false}>
        <div className="p-5 pb-3">
          <h3 className="text-h3 text-[var(--text-primary)]">{t('aiPoints.versions')}</h3>
          <p className="text-body-sm text-muted">{t('aiPoints.versionsHint')}</p>
        </div>
        {data.versions.length === 0 ? (
          <p className="px-5 pb-5 text-body-sm text-muted">{t('aiPoints.noVersions')}</p>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>{t('aiPoints.version')}</Th>
                <Th>{t('aiPoints.savedBy')}</Th>
                <Th>{t('aiPoints.when')}</Th>
              </tr>
            </thead>
            <tbody>
              {data.versions.map((v) => (
                <Tr key={v.version}>
                  <Td className="tabular">{v.version}</Td>
                  <Td>{v.createdBy ? localized(v.createdBy) : '—'}</Td>
                  <Td className="tabular">{formatDateTime(v.createdAt, locale)}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}
